import type { AxiosEventMap } from '@shared/axios/axiosEventBus.ts';
import { isProvisioningLockoutErrorCode } from '../errors';
import axios from 'axios';
import { useEffect, useState } from 'react';

type Subscribe = (param: keyof AxiosEventMap, handler: (err: unknown) => void) => void;

export type ProvisioningLockout = {
  /** Server trace id, so support can find the blocked identity in the logs. May be absent. */
  traceId?: string;
};

/**
 * EWM-1066: watches the axios error stream for a provisioning lockout - a token that authenticated
 * but cannot become a user row (no email claim, or no given_name/family_name claim).
 *
 * Every bootstrap request fails together in that case, so this latches on the first one and stays
 * latched: the condition is terminal until the IdP / B2C claim mapping is fixed upstream, and
 * clearing it would just let the splash screen resume waiting forever. `AxiosErrorHandler`
 * suppresses the matching toasts so the caller can render one full-page state instead of ten.
 */
export const useProvisioningLockout = (
  subscribe: Subscribe,
  unsubscribe: Subscribe,
): ProvisioningLockout | null => {
  const [lockout, setLockout] = useState<ProvisioningLockout | null>(null);

  useEffect(() => {
    const handleResponseError = (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        return;
      }

      const data = error.response?.data as { errorCode?: unknown; traceId?: unknown } | undefined;

      if (!isProvisioningLockoutErrorCode(data?.errorCode)) {
        return;
      }

      setLockout((current) => {
        // First failure wins - the other nine carry the same cause and no better trace id.
        if (current) {
          return current;
        }

        return { traceId: typeof data?.traceId === 'string' ? data.traceId : undefined };
      });
    };

    subscribe('responseError', handleResponseError);
    return (): void => unsubscribe('responseError', handleResponseError);
  }, [subscribe, unsubscribe]);

  return lockout;
};
